export const nutritionalDiagnosisOptions = [
  "Peso adecuado, grasa y masa magra dentro de parámetros normales.",
  "Peso adecuado, grasa corporal dentro de parámetros normales, bajo desarrollo muscular",
  "Peso adecuado, exceso de grasa corporal y bajo desarrollo muscular",
  "Peso adecuado con óptimo desarrollo muscular y exceso leve de grasa corporal",
  "Sobrepeso a expensas de alto desarrollo muscular",
  "Sobrepeso a expensas de exceso moderado de grasa corporal y de alto desarrollo muscular",
  "Obesidad",
  "Bajo peso",
  "Bajo peso, déficit de masa magra",
  "Desnutrición",
];

export const adultAnthropometryMethodOptions = [
  { value: "yuhasz", label: "Dos componentes - Yuhasz" },
  { value: "durnin_siri", label: "Dos componentes - Durnin + Siri" },
  { value: "rfm", label: "Masa grasa relativa" },
  { value: "sumatoria_pliegues", label: "Sumatoria de pliegues" },
  { value: "cinco_componentes", label: "Cinco componentes - Ross & Kerr" },
];

export const pediatricAnthropometryMethodOptions = [
  { value: "slaughter", label: "Dos componentes pediátrico - Slaughter" },
  { value: "parizkova", label: "Dos componentes pediátrico - Parízková" },
  { value: "brook_lohman", label: "Dos componentes 1-11 años - Brook + Lohman" },
  { value: "durnin_rahaman_lohman", label: "Dos componentes 12-16 años - Durnin & Rahaman + Lohman" },
  { value: "sumatoria_pliegues", label: "Sumatoria de pliegues" },
  { value: "cinco_componentes", label: "Cinco componentes - Ross & Kerr" },
];

export const anthropometryMethodOptions = [
  ...adultAnthropometryMethodOptions,
  ...pediatricAnthropometryMethodOptions.filter(
    (option) => !adultAnthropometryMethodOptions.some((candidate) => candidate.value === option.value),
  ),
];

const anthropometryMethodIds = new Set(anthropometryMethodOptions.map((option) => option.value));
const anthropometryMethodAliases = new Map([
  ["5_componentes", "cinco_componentes"],
  ["five_component", "cinco_componentes"],
  ["five_components", "cinco_componentes"],
  ["ross_kerr", "cinco_componentes"],
  ["absolute_sums", "sumatoria_pliegues"],
  ["skinfold_sums", "sumatoria_pliegues"],
  ["sumatoria_de_pliegues", "sumatoria_pliegues"],
]);

export function normalizeAnthropometryMethod(value, fallback = "yuhasz") {
  const normalized = String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const resolved = anthropometryMethodAliases.get(normalized) || normalized;
  return anthropometryMethodIds.has(resolved) ? resolved : fallback;
}

export const leanMassSourceOptions = [
  { value: "metodo_principal", label: "Masa libre del método principal" },
  { value: "rfm", label: "Masa libre por masa grasa relativa" },
  { value: "durnin_siri", label: "Peso magro por Durnin + Siri" },
  { value: "cinco_componentes", label: "Peso magro por cinco componentes" },
];

export const anthropometryGoalFieldNames = [
  "fuente_masa_libre_grasa",
  "meta_yuhasz_suma_6_pliegues_mm",
  "meta_yuhasz_cambio_muscular_kg",
  "meta_tronco_suma_6_pliegues_mm",
  "meta_durnin_suma_4_pliegues_mm",
  "meta_durnin_cambio_muscular_kg",
  "meta_rfm_cintura_ideal_cm",
  "meta_5c_suma_pliegues_ideal_mm",
  "meta_5c_objetivo_imo",
];

export const DEFAULT_ANTHROPOMETRY_SECTIONS = [
  "basicas",
  "crecimiento",
  "pliegues",
  "perimetros",
  "longitudes",
  "diametros",
  "otras",
];

export const anthropometrySectionOptions = [
  {
    id: "basicas",
    label: "Medidas básicas",
    description: "Datos generales usados como base de todos los cálculos.",
    fields: [
      { label: "Peso actual kg", name: "peso_kg", type: "number" },
      { label: "Talla cm", name: "talla_cm", type: "number" },
      { label: "Estatura sentado cm", name: "estatura_sentado_cm", type: "number" },
      { label: "Envergadura cm", name: "envergadura_cm", type: "number" },
      { label: "Peso objetivo kg", name: "peso_objetivo_kg", type: "number" },
      { label: "Fuente masa libre de grasa", name: "fuente_masa_libre_grasa", type: "text", options: leanMassSourceOptions },
    ],
  },
  {
    id: "crecimiento",
    label: "Crecimiento y maduración",
    description: "Campos pediátricos para interpretar crecimiento, maduración y proyección de estatura.",
    fields: [
      {
        label: "Etapa Tanner",
        name: "etapa_tanner",
        type: "text",
        options: [
          { value: "", label: "Sin registrar" },
          { value: "1", label: "Tanner 1" },
          { value: "2", label: "Tanner 2" },
          { value: "3", label: "Tanner 3" },
          { value: "4", label: "Tanner 4" },
          { value: "5", label: "Tanner 5" },
        ],
      },
      { label: "Z-score IMC para la edad", name: "imc_edad_zscore", type: "number" },
      { label: "Z-score talla para la edad", name: "talla_edad_zscore", type: "number" },
      { label: "Centímetros estimados por crecer", name: "centimetros_por_crecer_cm", type: "number" },
    ],
  },
  {
    id: "pliegues",
    label: "Pliegues",
    description: "Pliegues cutáneos en milímetros.",
    fields: [
      { label: "Submandibular", name: "pliegue_submandibular_mm", type: "number" },
      { label: "Tríceps", name: "pliegue_triceps_mm", type: "number" },
      { label: "Bíceps", name: "pliegue_biceps_mm", type: "number" },
      { label: "Subescapular", name: "pliegue_subescapular_mm", type: "number" },
      { label: "Cresta ilíaca", name: "pliegue_cresta_iliaca_mm", type: "number" },
      { label: "Supraespinal", name: "pliegue_supraespinal_mm", type: "number" },
      { label: "Abdominal", name: "pliegue_abdominal_mm", type: "number" },
      { label: "Muslo", name: "pliegue_muslo_mm", type: "number" },
      { label: "Pantorrilla", name: "pliegue_pantorrilla_mm", type: "number" },
      { label: "Supraespinal izquierdo (opcional)", name: "pliegue_supraespinal_izquierdo_mm", type: "number" },
      { label: "Abdominal izquierdo (opcional)", name: "pliegue_abdominal_izquierdo_mm", type: "number" },
      { label: "Subescapular izquierdo (opcional)", name: "pliegue_subescapular_izquierdo_mm", type: "number" },
    ],
  },
  {
    id: "perimetros",
    label: "Perímetros",
    description: "Perímetros corporales en centímetros.",
    fields: [
      { label: "Cefálico", name: "perimetro_cefalico_cm", type: "number" },
      { label: "Cuello", name: "perimetro_cuello_cm", type: "number" },
      { label: "Brazo relajado", name: "perimetro_brazo_relajado_cm", type: "number" },
      { label: "Brazo relajado 2", name: "perimetro_brazo_relajado_2_cm", type: "number" },
      { label: "Brazo tenso", name: "perimetro_brazo_tenso_cm", type: "number" },
      { label: "Antebrazo máximo", name: "perimetro_antebrazo_cm", type: "number" },
      { label: "Antebrazo izquierdo", name: "perimetro_antebrazo_izquierdo_cm", type: "number" },
      { label: "Muñeca", name: "perimetro_muneca_cm", type: "number" },
      { label: "Tórax", name: "perimetro_torax_cm", type: "number" },
      { label: "Cintura", name: "perimetro_cintura_cm", type: "number" },
      { label: "Abdomen", name: "perimetro_abdomen_cm", type: "number" },
      { label: "Caderas", name: "perimetro_cadera_cm", type: "number" },
      { label: "Muslo superior", name: "perimetro_muslo_superior_cm", type: "number" },
      { label: "Muslo superior izquierdo", name: "perimetro_muslo_superior_izquierdo_cm", type: "number" },
      { label: "Muslo medio", name: "perimetro_muslo_medio_cm", type: "number" },
      { label: "Pantorrilla", name: "perimetro_pantorrilla_cm", type: "number" },
      { label: "Tobillo", name: "perimetro_tobillo_cm", type: "number" },
    ],
  },
  {
    id: "longitudes",
    label: "Longitudes",
    description: "Longitudes segmentarias en centímetros.",
    fields: [
      { label: "Acromiale-Radiale", name: "longitud_acromiale_radiale_cm", type: "number" },
      { label: "Radiale-Stylion", name: "longitud_radiale_stylion_cm", type: "number" },
      { label: "Stylion Medio-Dactylion", name: "longitud_stylion_medio_dactylion_cm", type: "number" },
      { label: "Ilioespinale", name: "longitud_ilioespinale_cm", type: "number" },
      { label: "Trochanterion", name: "longitud_trochanterion_cm", type: "number" },
      { label: "Trochanterion-Tibiale laterale", name: "longitud_trochanterion_tibiale_laterale_cm", type: "number" },
      { label: "Tibiale laterale", name: "longitud_tibiale_laterale_cm", type: "number" },
      { label: "Pie", name: "longitud_pie_cm", type: "number" },
      { label: "Tibiale mediale-Sphyrion tibiale", name: "longitud_tibiale_mediale_sphyrion_tibiale_cm", type: "number" },
    ],
  },
  {
    id: "diametros",
    label: "Diámetros",
    description: "Diámetros óseos y corporales en centímetros.",
    fields: [
      { label: "Biacromial", name: "diametro_biacromial_cm", type: "number" },
      { label: "Bicristal", name: "diametro_biliocrestal_cm", type: "number" },
      { label: "Bitrocantéreo", name: "diametro_bitrocantereo_cm", type: "number" },
      { label: "Transversal tórax", name: "diametro_torax_transverso_cm", type: "number" },
      { label: "AP tórax", name: "diametro_torax_ap_cm", type: "number" },
      { label: "Biestiloideo", name: "diametro_biestiloideo_cm", type: "number" },
      { label: "Húmero", name: "diametro_humero_cm", type: "number" },
      { label: "Fémur", name: "diametro_femur_cm", type: "number" },
      { label: "Bimaleolar", name: "diametro_bimaleolar_cm", type: "number" },
      { label: "Pie", name: "diametro_pie_cm", type: "number" },
      { label: "AP abdomen", name: "diametro_abdomen_ap_cm", type: "number" },
    ],
  },
  {
    id: "otras",
    label: "Otras medidas",
    description: "Medidas alternativas o de respaldo.",
    fields: [
      { label: "Media envergadura", name: "media_envergadura_cm", type: "number" },
      { label: "Semi brazada", name: "semi_brazada_cm", type: "number" },
      { label: "Antebrazo", name: "longitud_antebrazo_cm", type: "number" },
      { label: "Rodilla-maleolo", name: "rodilla_maleolo_cm", type: "number" },
      { label: "Rodilla-talón", name: "rodilla_talon_cm", type: "number" },
    ],
  },
];

export const anthropometryInputFields = new Set([
  "sexo",
  "deporte",
  "population_profile",
  "fecha_nacimiento",
  "fecha_evaluacion",
  "metodo_antropometria",
  "numero_valoracion",
  "etapa_entrenamiento",
  "antropometria_secciones_activas",
  "asumir_simetria_tronco",
  "referencia_imo_min", "referencia_imo_max", "referencia_iam_min", "referencia_iam_max", "referencia_aks_min", "referencia_aks_max", "referencia_indices_fuente",
  ...anthropometryGoalFieldNames,
  ...anthropometrySectionOptions.flatMap((section) => section.fields.map((field) => field.name)),
]);

const quickFieldsByMethod = {
  yuhasz: [
    "peso_kg",
    "talla_cm",
    "pliegue_triceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_supraespinal_mm",
    "pliegue_abdominal_mm",
    "pliegue_muslo_mm",
    "pliegue_pantorrilla_mm",
  ],
  durnin_siri: [
    "peso_kg",
    "talla_cm",
    "pliegue_triceps_mm",
    "pliegue_biceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_cresta_iliaca_mm",
  ],
  rfm: [
    "peso_kg",
    "talla_cm",
    "perimetro_cintura_cm",
  ],
  sumatoria_pliegues: [
    "peso_kg",
    "talla_cm",
    "pliegue_submandibular_mm",
    "pliegue_triceps_mm",
    "pliegue_biceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_cresta_iliaca_mm",
    "pliegue_supraespinal_mm",
    "pliegue_abdominal_mm",
    "pliegue_muslo_mm",
    "pliegue_pantorrilla_mm",
  ],
  slaughter: [
    "peso_kg",
    "talla_cm",
    "etapa_tanner",
    "pliegue_triceps_mm",
    "pliegue_subescapular_mm",
  ],
  parizkova: [
    "peso_kg",
    "talla_cm",
    "pliegue_triceps_mm",
    "pliegue_biceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_cresta_iliaca_mm",
  ],
  brook_lohman: [
    "peso_kg",
    "talla_cm",
    "pliegue_triceps_mm",
    "pliegue_biceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_supraespinal_mm",
  ],
  durnin_rahaman_lohman: [
    "peso_kg",
    "talla_cm",
    "pliegue_triceps_mm",
    "pliegue_biceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_supraespinal_mm",
  ],
  cinco_componentes: [
    "peso_kg",
    "talla_cm",
    "estatura_sentado_cm",
    "pliegue_triceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_supraespinal_mm",
    "pliegue_abdominal_mm",
    "pliegue_muslo_mm",
    "pliegue_pantorrilla_mm",
    "perimetro_cefalico_cm",
    "perimetro_brazo_relajado_cm",
    "perimetro_antebrazo_cm",
    "perimetro_torax_cm",
    "perimetro_cintura_cm",
    "perimetro_muslo_medio_cm",
    "perimetro_pantorrilla_cm",
    "diametro_biacromial_cm",
    "diametro_biliocrestal_cm",
    "diametro_torax_transverso_cm",
    "diametro_torax_ap_cm",
    "diametro_humero_cm",
    "diametro_femur_cm",
  ],
};

const somatotypeQuickFields = [
  "peso_kg",
  "talla_cm",
  "pliegue_triceps_mm",
  "pliegue_subescapular_mm",
  "pliegue_supraespinal_mm",
  "pliegue_pantorrilla_mm",
  "perimetro_brazo_tenso_cm",
  "perimetro_pantorrilla_cm",
  "diametro_humero_cm",
  "diametro_femur_cm",
];

const goalFieldsByMethod = {
  yuhasz: [
    { name: "meta_yuhasz_suma_6_pliegues_mm", label: "Σ6 pliegues objetivo Yuhasz mm" },
    { name: "meta_yuhasz_cambio_muscular_kg", label: "Cambio muscular Yuhasz kg" },
    { name: "meta_tronco_suma_6_pliegues_mm", label: "Σ6 pliegues de tronco objetivo mm" },
  ],
  durnin_siri: [
    { name: "meta_durnin_suma_4_pliegues_mm", label: "Σ4 pliegues objetivo Durnin mm" },
    { name: "meta_durnin_cambio_muscular_kg", label: "Cambio muscular Durnin kg" },
    { name: "meta_tronco_suma_6_pliegues_mm", label: "Σ6 pliegues de tronco objetivo mm" },
  ],
  rfm: [
    { name: "meta_rfm_cintura_ideal_cm", label: "Cintura ideal RFM cm" },
  ],
  cinco_componentes: [
    { name: "meta_5c_suma_pliegues_ideal_mm", label: "Σ6 ideal cinco componentes mm" },
    { name: "meta_5c_objetivo_imo", label: "Objetivo IMO cinco componentes" },
    { name: "meta_tronco_suma_6_pliegues_mm", label: "Σ6 pliegues de tronco objetivo mm" },
  ],
  sumatoria_pliegues: [
    { name: "meta_tronco_suma_6_pliegues_mm", label: "Σ6 pliegues de tronco objetivo mm" },
  ],
};

const anthropometryGoalPercentiles = {
  meta_yuhasz_suma_6_pliegues_mm: {
    masculino: [[5, 33.6], [15, 47.1], [25, 52.5], [50, 65.6], [75, 84.2], [85, 94.3], [95, 115.9]],
    femenino: [[5, 61.9], [15, 69.5], [25, 76.4], [50, 91.5], [75, 112.4], [85, 121.6], [95, 145.2]],
  },
  meta_durnin_suma_4_pliegues_mm: {
    masculino: [[5, 17.1], [15, 21], [25, 25.1], [50, 30], [75, 40.9], [85, 46.6], [95, 64.2]],
    femenino: [[5, 23.8], [15, 28.7], [25, 32.9], [50, 42.5], [75, 51.4], [85, 58.9], [95, 74]],
  },
  meta_tronco_suma_6_pliegues_mm: {
    masculino: [[5, 30.2], [15, 38.7], [25, 48.3], [50, 61], [75, 84.5], [85, 97.4], [95, 132.8]],
    femenino: [[5, 41.9], [15, 53.6], [25, 60.7], [50, 77.8], [75, 98.9], [85, 111.1], [95, 142]],
  },
};

export function getAnthropometryGoalHint(fieldName, sex) {
  if (fieldName === "meta_5c_suma_pliegues_ideal_mm") {
    return sex === "masculino"
      ? "Referencia teórica del formato: 33,6–52,6 mm."
      : sex === "femenino"
        ? "Referencia teórica del formato: 61,9–76,4 mm."
        : "Selecciona sexo para ver la referencia teórica del formato.";
  }
  if (fieldName === "meta_5c_objetivo_imo") {
    return "El IMO objetivo define la masa muscular esperada respecto a la masa ósea.";
  }
  if (fieldName.includes("cambio_muscular")) {
    return "Usa un valor positivo para ganancia y negativo para pérdida muscular prevista.";
  }
  const rows = anthropometryGoalPercentiles[fieldName]?.[sex];
  if (!rows) return "Selecciona sexo para consultar los percentiles de referencia del formato.";
  return rows.map(([percentile, value]) => `P${percentile}: ${String(value).replace(".", ",")}`).join(" · ");
}

export function getAnthropometryGoalFields(method) {
  return goalFieldsByMethod[normalizeAnthropometryMethod(method)] ?? [];
}

export function anthropometryFieldDisplayLabel(field) {
  const label = String(field?.label || "");
  const name = String(field?.name || "");
  const unit = name.endsWith("_mm") ? "mm"
    : name.endsWith("_cm") ? "cm"
      : name.endsWith("_kg") ? "kg"
        : "";
  if (!unit || new RegExp(`\\b${unit}\\b`, "i").test(label)) return label;
  return `${label} (${unit})`;
}

export function parseAnthropometryPasteValues(value) {
  const source = String(value ?? "").trim();
  if (!source) return [];
  const separated = /[\t;\r\n]/.test(source);
  const candidates = separated
    ? source
      .split(/[\t;\r\n]+/)
      .map((segment) => segment.match(/-?\d+(?:[.,]\d+)?/)?.[0])
    : source.match(/-?\d+(?:[.,]\d+)?/g);
  return (candidates ?? [])
    .map((candidate) => Number(String(candidate).replace(",", ".")))
    .filter((candidate) => Number.isFinite(candidate) && candidate >= 0)
    .map(String);
}

export function getAnthropometryQuickCaptureFields(method, { includeSomatotype = true } = {}) {
  const normalizedMethod = normalizeAnthropometryMethod(method);
  const methodFields = new Set(quickFieldsByMethod[normalizedMethod] ?? quickFieldsByMethod.yuhasz);
  const selected = new Set(methodFields);
  if (includeSomatotype) {
    somatotypeQuickFields.forEach((fieldName) => selected.add(fieldName));
  }
  return anthropometrySectionOptions.flatMap((section) => section.fields
    .filter((field) => selected.has(field.name))
    .map((field) => ({
      ...field,
      sectionId: section.id,
      sectionLabel: section.label,
      usedByMethod: methodFields.has(field.name),
      usedBySomatotype: somatotypeQuickFields.includes(field.name),
    })));
}

const fieldSectionByName = new Map(
  anthropometrySectionOptions.flatMap((section) => section.fields.map((field) => [field.name, section.id])),
);

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function positiveNumber(value) {
  const parsed = toNumber(value);
  return parsed > 0 ? parsed : 0;
}

function optionalNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function round(value, decimals = 1) {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function parseDateOnly(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const text = String(value).trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

export function calculateAgeYears(fechaNacimiento, fechaReferencia = new Date()) {
  const birth = parseDateOnly(fechaNacimiento);
  const reference = parseDateOnly(fechaReferencia) || parseDateOnly(new Date());
  if (!birth || !reference || birth > reference) return 0;
  const millisecondsPerYear = 365.25 * 24 * 60 * 60 * 1000;
  return round((reference.getTime() - birth.getTime()) / millisecondsPerYear, 1);
}

export function resolvePatientAge(patient, fechaReferencia = new Date()) {
  const calculated = calculateAgeYears(patient?.fecha_nacimiento, patient?.fecha_evaluacion || fechaReferencia);
  return calculated || positiveNumber(patient?.edad);
}

export function normalizeAnthropometrySections(value) {
  const allowed = new Set(DEFAULT_ANTHROPOMETRY_SECTIONS);
  let selected = [];
  if (Array.isArray(value)) {
    selected = value;
  } else if (value && typeof value === "object") {
    selected = Object.entries(value).filter(([, isActive]) => isActive).map(([id]) => id);
  } else if (typeof value === "string") {
    selected = value.split(/[;,|]/);
  }
  const normalized = [...new Set(selected.map((item) => String(item ?? "").trim()).filter((item) => allowed.has(item)))];
  return normalized.length ? normalized : [...DEFAULT_ANTHROPOMETRY_SECTIONS];
}

export function getActiveAnthropometrySections(patient) {
  return normalizeAnthropometrySections(patient?.antropometria_secciones_activas);
}

export function isAnthropometrySectionActive(patient, sectionId) {
  return getActiveAnthropometrySections(patient).includes(sectionId);
}

function patientNumber(patient, fieldName) {
  const sectionId = fieldSectionByName.get(fieldName);
  if (sectionId && !isAnthropometrySectionActive(patient, sectionId)) return 0;
  return positiveNumber(patient?.[fieldName]);
}

function patientOptionalNumber(patient, fieldName) {
  const sectionId = fieldSectionByName.get(fieldName);
  if (sectionId && !isAnthropometrySectionActive(patient, sectionId)) return null;
  return optionalNumber(patient?.[fieldName]);
}

function sexIsMale(patient) {
  const value = String(patient?.sexo ?? "").trim().toLowerCase();
  return value === "masculino" || value === "m" || value === "male" || value === "hombre";
}

function sexIsFemale(patient) {
  const value = String(patient?.sexo ?? "").trim().toLowerCase();
  return value === "femenino" || value === "f" || value === "female" || value === "mujer";
}

function fatMassFromPercent(weight, percent) {
  if (!weight || !percent) return null;
  const fatKg = round((percent * weight) / 100, 2);
  return {
    fatKg,
    leanKg: round(weight - fatKg, 2),
  };
}

function listMissing(patient, fieldNames) {
  return fieldNames.filter((fieldName) => !patientNumber(patient, fieldName));
}

function calculateSkinfoldSums(patient) {
  const skinfolds = {
    submandibular: patientNumber(patient, "pliegue_submandibular_mm"),
    triceps: patientNumber(patient, "pliegue_triceps_mm"),
    biceps: patientNumber(patient, "pliegue_biceps_mm"),
    subscapular: patientNumber(patient, "pliegue_subescapular_mm"),
    crest: patientNumber(patient, "pliegue_cresta_iliaca_mm"),
    supraespinal: patientNumber(patient, "pliegue_supraespinal_mm"),
    abdominal: patientNumber(patient, "pliegue_abdominal_mm"),
    thigh: patientNumber(patient, "pliegue_muslo_mm"),
    calf: patientNumber(patient, "pliegue_pantorrilla_mm"),
  };
  const values = Object.values(skinfolds).filter((value) => value > 0);
  const sum = (...items) => round(items.reduce((total, value) => total + value, 0), 1);
  const total = sum(...values);
  if (!total) {
    return {
      method: "sumatoria_pliegues",
      label: "Sumatoria de pliegues",
      complete: false,
      missing: ["pliegue_triceps_mm", "pliegue_subescapular_mm", "pliegue_supraespinal_mm"],
    };
  }
  const sum3Somatotype = sum(skinfolds.triceps, skinfolds.subscapular, skinfolds.supraespinal);
  const completeSum = (items) => items.every((value) => value > 0) ? sum(...items) : null;
  const sum4Durnin = completeSum([skinfolds.triceps, skinfolds.biceps, skinfolds.subscapular, skinfolds.crest]);
  const sum6Yuhasz = sum(skinfolds.triceps, skinfolds.subscapular, skinfolds.supraespinal, skinfolds.abdominal, skinfolds.thigh, skinfolds.calf);
  return {
    method: "sumatoria_pliegues",
    label: "Sumatoria de pliegues",
    complete: true,
    totalSkinfolds: total,
    measuredSkinfoldCount: values.length,
    sum3Somatotype,
    sum4Durnin,
    sum6Yuhasz,
    sum6Complete: completeSum([skinfolds.triceps, skinfolds.subscapular, skinfolds.supraespinal, skinfolds.abdominal, skinfolds.thigh, skinfolds.calf]),
    sum6Upper: completeSum([skinfolds.triceps, skinfolds.biceps, skinfolds.subscapular, skinfolds.crest, skinfolds.supraespinal, skinfolds.abdominal]),
    sum8: completeSum([skinfolds.triceps, skinfolds.biceps, skinfolds.subscapular, skinfolds.crest, skinfolds.supraespinal, skinfolds.abdominal, skinfolds.thigh, skinfolds.calf]),
    summaryValue: `${total} mm`,
    detail: `${values.length} pliegues medidos. Suma 6 Yuhasz ${sum6Yuhasz} mm; suma 4 Durnin ${sum4Durnin} mm.`,
  };
}

function calculateYuhasz(patient) {
  const required = [
    "pliegue_triceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_supraespinal_mm",
    "pliegue_abdominal_mm",
    "pliegue_muslo_mm",
    "pliegue_pantorrilla_mm",
  ];
  const weight = patientNumber(patient, "peso_kg");
  const triceps = patientNumber(patient, "pliegue_triceps_mm");
  const subscapular = patientNumber(patient, "pliegue_subescapular_mm");
  const supraespinal = patientNumber(patient, "pliegue_supraespinal_mm");
  const abdominal = patientNumber(patient, "pliegue_abdominal_mm");
  const thigh = patientNumber(patient, "pliegue_muslo_mm");
  const calf = patientNumber(patient, "pliegue_pantorrilla_mm");
  const sum6 = triceps + subscapular + supraespinal + abdominal + thigh + calf;
  const missing = listMissing(patient, required);
  if (!sexIsMale(patient) && !sexIsFemale(patient)) missing.push('sexo');
  if (missing.length) {
    return {
      method: "yuhasz",
      label: "Yuhasz",
      complete: false,
      sum6: round(sum6, 1),
      missing,
    };
  }
  const fatPercent = sexIsMale(patient)
    ? (sum6 * 0.1051) + 2.585
    : (sum6 * 0.1548) + 3.58;
  if (!Number.isFinite(fatPercent) || fatPercent <= 0 || fatPercent >= 100) {
    return { method:'yuhasz',label:'Yuhasz',complete:false,invalid:true,missing:[] };
  }
  return {
    method: "yuhasz",
    label: "Yuhasz",
    complete: true,
    sum6: round(sum6, 1),
    fatPercent: round(fatPercent, 1),
    ...fatMassFromPercent(weight, fatPercent),
    formula: sexIsMale(patient) ? "sumatoria_6 * 0.1051 + 2.585" : "sumatoria_6 * 0.1548 + 3.58",
  };
}

export function durninCoefficients(patient) {
  const age = resolvePatientAge(patient);
  if (age < 16 || age > 72 || (!sexIsMale(patient) && !sexIsFemale(patient))) return null;
  const band = age < 20 ? 0 : age < 30 ? 1 : age < 40 ? 2 : age < 50 ? 3 : 4;
  const coefficients = sexIsMale(patient)
    ? [[1.162, 0.063], [1.1631, 0.0632], [1.1422, 0.0544], [1.162, 0.07], [1.1715, 0.0779]]
    : [[1.1549, 0.0678], [1.1599, 0.0717], [1.1423, 0.0632], [1.1333, 0.0612], [1.1339, 0.0645]];
  return { c: coefficients[band][0], m: coefficients[band][1], band: ['16–19','20–29','30–39','40–49','50–72'][band] };
}

function calculateDurninSiri(patient) {
  const required = [
    "pliegue_triceps_mm",
    "pliegue_biceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_cresta_iliaca_mm",
  ];
  const weight = patientNumber(patient, "peso_kg");
  const triceps = patientNumber(patient, "pliegue_triceps_mm");
  const biceps = patientNumber(patient, "pliegue_biceps_mm");
  const subscapular = patientNumber(patient, "pliegue_subescapular_mm");
  const crest = patientNumber(patient, "pliegue_cresta_iliaca_mm");
  const sum4 = triceps + biceps + subscapular + crest;
  const missing = listMissing(patient, required);
  const coefficients = durninCoefficients(patient);
  if (missing.length || !coefficients) {
    return {
      method: "durnin_siri",
      label: "Durnin + Siri",
      complete: false,
      sum4: round(sum4, 1),
      missing,
      applicable: Boolean(coefficients),
      applicabilityNote: coefficients ? '' : 'Durnin–Womersley requiere edad entre 16 y 72 años y sexo de referencia registrado.',
    };
  }
  const density = coefficients.c - (coefficients.m * Math.log10(sum4));
  const fatPercent = ((4.95 / density) - 4.5) * 100;
  if (!Number.isFinite(fatPercent) || fatPercent <= 0 || fatPercent >= 100) {
    return { method:'durnin_siri',label:'Durnin + Siri',complete:false,invalid:true,missing:[],coefficients };
  }
  return {
    method: "durnin_siri",
    label: "Durnin + Siri",
    complete: true,
    sum4: round(sum4, 1),
    density: round(density, 4),
    fatPercent: round(fatPercent, 1),
    ...fatMassFromPercent(weight, fatPercent),
    coefficients,
    formula: `DC = ${coefficients.c} - ${coefficients.m} * LOG10(Σ4 cresta ilíaca); Siri = ((4.95 / DC) - 4.5) * 100`,
  };
}

function pediatricCompositionResult({
  method,
  label,
  weight,
  fatPercent,
  formula,
  values = {},
  applicable = true,
  applicabilityNote = "",
}) {
  if (!weight || !Number.isFinite(fatPercent) || fatPercent <= 0) {
    return {
      method,
      label,
      complete: false,
      applicable,
      applicabilityNote,
      ...values,
    };
  }
  return {
    method,
    label,
    complete: true,
    applicable,
    applicabilityNote,
    fatPercent: round(fatPercent, 1),
    ...fatMassFromPercent(weight, fatPercent),
    ...values,
    formula,
  };
}

function calculateParizkova(patient) {
  const weight = patientNumber(patient, "peso_kg");
  const triceps = patientNumber(patient, "pliegue_triceps_mm");
  const biceps = patientNumber(patient, "pliegue_biceps_mm");
  const subscapular = patientNumber(patient, "pliegue_subescapular_mm");
  const iliacCrest = patientNumber(patient, "pliegue_cresta_iliaca_mm");
  const sum4 = triceps + biceps + subscapular + iliacCrest;
  if (!weight || !triceps || !biceps || !subscapular || !iliacCrest) {
    return {
      method: "parizkova",
      label: "Parízková",
      complete: false,
      missing: listMissing(patient, [
        "peso_kg",
        "pliegue_triceps_mm",
        "pliegue_biceps_mm",
        "pliegue_subescapular_mm",
        "pliegue_cresta_iliaca_mm",
      ]),
    };
  }
  const fatPercent = 2.745
    + (0.02 * subscapular)
    + (0.08 * triceps)
    + (0.8 * biceps)
    + (0.637 * iliacCrest);
  return pediatricCompositionResult({
    method: "parizkova",
    label: "Parízková",
    weight,
    fatPercent,
    values: { sum4: round(sum4, 1) },
    formula: "% grasa = 2.745 + 0.02 × subescapular + 0.08 × tríceps + 0.8 × bíceps + 0.637 × cresta ilíaca",
  });
}

function calculateBrookLohman(patient) {
  const required = [
    "pliegue_triceps_mm",
    "pliegue_biceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_supraespinal_mm",
  ];
  const weight = patientNumber(patient, "peso_kg");
  const age = resolvePatientAge(patient);
  const triceps = patientNumber(patient, "pliegue_triceps_mm");
  const biceps = patientNumber(patient, "pliegue_biceps_mm");
  const subscapular = patientNumber(patient, "pliegue_subescapular_mm");
  const supraespinal = patientNumber(patient, "pliegue_supraespinal_mm");
  const sum4 = triceps + biceps + subscapular + supraespinal;
  const missing = listMissing(patient, required);
  if (!weight || missing.length) {
    return {
      method: "brook_lohman",
      label: "Brook + Lohman",
      complete: false,
      sum4: round(sum4, 1),
      missing: [
        ...(!weight ? ["peso_kg"] : []),
        ...missing,
      ],
      applicable: age > 0 && age < 12,
      applicabilityNote: "El formato actualizado reserva esta ecuación para edades de 1 a 11 años.",
    };
  }
  const density = sexIsMale(patient)
    ? 1.169 - (0.0788 * Math.log10(sum4))
    : 1.2063 - (0.0999 * Math.log10(sum4));
  const fatPercent = ((5.3 / density) - 4.89) * 100;
  return pediatricCompositionResult({
    method: "brook_lohman",
    label: "Brook + Lohman",
    weight,
    fatPercent,
    applicable: age > 0 && age < 12,
    applicabilityNote: "Aplicación de referencia: 1 a 11 años.",
    values: { sum4: round(sum4, 1), density: round(density, 4) },
    formula: sexIsMale(patient)
      ? "DC = 1.169 - 0.0788 × LOG10(Σ4); % grasa Lohman = (5.3 / DC - 4.89) × 100"
      : "DC = 1.2063 - 0.0999 × LOG10(Σ4); % grasa Lohman = (5.3 / DC - 4.89) × 100",
  });
}

function calculateDurninRahamanLohman(patient) {
  const required = [
    "pliegue_triceps_mm",
    "pliegue_biceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_supraespinal_mm",
  ];
  const weight = patientNumber(patient, "peso_kg");
  const age = resolvePatientAge(patient);
  const triceps = patientNumber(patient, "pliegue_triceps_mm");
  const biceps = patientNumber(patient, "pliegue_biceps_mm");
  const subscapular = patientNumber(patient, "pliegue_subescapular_mm");
  const supraespinal = patientNumber(patient, "pliegue_supraespinal_mm");
  const sum4 = triceps + biceps + subscapular + supraespinal;
  const missing = listMissing(patient, required);
  if (!weight || missing.length) {
    return {
      method: "durnin_rahaman_lohman",
      label: "Durnin & Rahaman + Lohman",
      complete: false,
      sum4: round(sum4, 1),
      missing: [
        ...(!weight ? ["peso_kg"] : []),
        ...missing,
      ],
      applicable: age >= 12 && age < 17,
      applicabilityNote: "El formato actualizado reserva esta ecuación para edades de 12 a 16 años.",
    };
  }
  const density = sexIsMale(patient)
    ? 1.1533 - (0.0643 * Math.log10(sum4))
    : 1.1369 - (0.0598 * Math.log10(sum4));
  const fatPercent = ((5.3 / density) - 4.89) * 100;
  return pediatricCompositionResult({
    method: "durnin_rahaman_lohman",
    label: "Durnin & Rahaman + Lohman",
    weight,
    fatPercent,
    applicable: age >= 12 && age < 17,
    applicabilityNote: "Aplicación de referencia: 12 a 16 años.",
    values: { sum4: round(sum4, 1), density: round(density, 4) },
    formula: sexIsMale(patient)
      ? "DC = 1.1533 - 0.0643 × LOG10(Σ4); % grasa Lohman = (5.3 / DC - 4.89) × 100"
      : "DC = 1.1369 - 0.0598 × LOG10(Σ4); % grasa Lohman = (5.3 / DC - 4.89) × 100",
  });
}

function calculateSlaughter(patient) {
  const weight = patientNumber(patient, "peso_kg");
  const triceps = patientNumber(patient, "pliegue_triceps_mm");
  const subscapular = patientNumber(patient, "pliegue_subescapular_mm");
  const sum2 = triceps + subscapular;
  const tanner = Math.max(1, Math.min(5, Math.round(patientNumber(patient, "etapa_tanner") || 1)));
  const maturationGroup = tanner <= 2 ? 1 : tanner === 3 ? 2 : 3;
  if (!weight || !triceps || !subscapular) {
    return {
      method: "slaughter",
      label: "Slaughter",
      complete: false,
      missing: listMissing(patient, ["peso_kg", "pliegue_triceps_mm", "pliegue_subescapular_mm"]),
      maturationGroup,
    };
  }
  let fatPercent;
  let formula;
  if (sum2 > 35) {
    fatPercent = sexIsMale(patient)
      ? (0.783 * sum2) + 1.6
      : (0.546 * sum2) + 9.7;
    formula = sexIsMale(patient)
      ? "% grasa = 0.783 × (tríceps + subescapular) + 1.6"
      : "% grasa = 0.546 × (tríceps + subescapular) + 9.7";
  } else if (sexIsMale(patient)) {
    const intercept = maturationGroup === 1 ? -1.7 : maturationGroup === 2 ? -3.4 : -5.5;
    fatPercent = (1.21 * sum2) - (0.008 * (sum2 ** 2)) + intercept;
    formula = `% grasa = 1.21 × Σ2 - 0.008 × Σ2² ${intercept < 0 ? "−" : "+"} ${Math.abs(intercept)}`;
  } else {
    fatPercent = (1.33 * sum2) - (0.013 * (sum2 ** 2)) - 2.5;
    formula = "% grasa = 1.33 × Σ2 - 0.013 × Σ2² - 2.5";
  }
  return pediatricCompositionResult({
    method: "slaughter",
    label: "Slaughter",
    weight,
    fatPercent,
    values: {
      sum2: round(sum2, 1),
      tannerStage: tanner,
      maturationGroup,
    },
    formula,
  });
}

function calculateRfm(patient) {
  const weight = patientNumber(patient, "peso_kg");
  const height = patientNumber(patient, "talla_cm");
  const waist = patientNumber(patient, "perimetro_cintura_cm");
  if (!height || !waist) return { method: "rfm", label: "Masa grasa relativa", complete: false };
  const fatPercent = sexIsMale(patient)
    ? 64 - (20 * height / waist)
    : 76 - (20 * height / waist);
  return {
    method: "rfm",
    label: "Masa grasa relativa",
    complete: true,
    fatPercent: round(fatPercent, 1),
    ...fatMassFromPercent(weight, fatPercent),
    formula: sexIsMale(patient) ? "64 - 20 * talla_cm / cintura_cm" : "76 - 20 * talla_cm / cintura_cm",
  };
}

function calculateFiveComponents(patient) {
  const required = [
    "peso_kg",
    "talla_cm",
    "estatura_sentado_cm",
    "pliegue_triceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_supraespinal_mm",
    "pliegue_abdominal_mm",
    "pliegue_muslo_mm",
    "pliegue_pantorrilla_mm",
    "perimetro_cefalico_cm",
    "perimetro_brazo_relajado_cm",
    "perimetro_antebrazo_cm",
    "perimetro_torax_cm",
    "perimetro_cintura_cm",
    "perimetro_muslo_medio_cm",
    "perimetro_pantorrilla_cm",
    "diametro_biacromial_cm",
    "diametro_biliocrestal_cm",
    "diametro_torax_transverso_cm",
    "diametro_torax_ap_cm",
    "diametro_humero_cm",
    "diametro_femur_cm",
  ];
  const missing = listMissing(patient, required);
  if (!sexIsMale(patient) && !sexIsFemale(patient)) missing.push('sexo');
  if (missing.length) {
    return {
      method: "cinco_componentes",
      label: "Cinco componentes",
      complete: false,
      missing,
    };
  }

  const weight = patientNumber(patient, "peso_kg");
  const height = patientNumber(patient, "talla_cm");
  const sittingHeight = patientNumber(patient, "estatura_sentado_cm");
  const triceps = patientNumber(patient, "pliegue_triceps_mm");
  const subscapular = patientNumber(patient, "pliegue_subescapular_mm");
  const supraespinal = patientNumber(patient, "pliegue_supraespinal_mm");
  const abdominal = patientNumber(patient, "pliegue_abdominal_mm");
  const thighSkinfold = patientNumber(patient, "pliegue_muslo_mm");
  const calfSkinfold = patientNumber(patient, "pliegue_pantorrilla_mm");
  const cephalic = patientNumber(patient, "perimetro_cefalico_cm");
  const armRelaxed = patientNumber(patient, "perimetro_brazo_relajado_cm");
  const forearm = patientNumber(patient, "perimetro_antebrazo_cm");
  const thorax = patientNumber(patient, "perimetro_torax_cm");
  const waist = patientNumber(patient, "perimetro_cintura_cm");
  const thigh = patientNumber(patient, "perimetro_muslo_medio_cm");
  const calf = patientNumber(patient, "perimetro_pantorrilla_cm");
  const biacromial = patientNumber(patient, "diametro_biacromial_cm");
  const bicristal = patientNumber(patient, "diametro_biliocrestal_cm");
  const thoraxTransverse = patientNumber(patient, "diametro_torax_transverso_cm");
  const thoraxAp = patientNumber(patient, "diametro_torax_ap_cm");
  const humerus = patientNumber(patient, "diametro_humero_cm");
  const femur = patientNumber(patient, "diametro_femur_cm");

  const surfaceArea = 68.308 * (weight ** 0.425) * ((height ** 0.725) / 10000);
  const boneBodySum = biacromial + bicristal + (2 * humerus) + (2 * femur);
  const headBoneZ = (cephalic - 56) / 1.44;
  const bodyBoneZ = ((boneBodySum * (170.18 / height)) - 98.88) / 5.33;
  const headBoneMass = (headBoneZ * 0.18) + 1.2;
  const bodyBoneMass = ((bodyBoneZ * 1.34) + 6.7) / ((170.18 / height) ** 3);
  const tricepsCorrection = triceps / 10;
  const subscapularCorrection = subscapular / 10;
  const abdominalCorrection = abdominal / 10;
  const thighCorrection = thighSkinfold / 10;
  const calfCorrection = calfSkinfold / 10;
  const correctedThorax = thorax - (3.1416 * subscapularCorrection);
  const correctedArm = armRelaxed - (3.1416 * tricepsCorrection);
  const correctedThigh = thigh - (3.1416 * thighCorrection);
  const correctedCalf = calf - (3.1416 * calfCorrection);
  const muscularPerimeterSum = correctedArm + forearm + correctedThigh + correctedCalf + correctedThorax;
  const residualSum = thoraxAp + thoraxTransverse + (waist - (3.1416 * abdominalCorrection));
  const skinfoldSum6 = triceps + subscapular + supraespinal + abdominal + thighSkinfold + calfSkinfold;
  const adiposeZ = ((skinfoldSum6 * (170.18 / height)) - 116.41) / 34.79;
  const muscularZ = ((muscularPerimeterSum * (170.18 / height)) - 207.21) / 13.74;
  const residualZ = ((residualSum * (89.92 / sittingHeight)) - 109.35) / 7.08;
  const skinThickness = sexIsMale(patient) ? 2.07 : 1.96;

  const skinKg = surfaceArea * skinThickness * 1.05;
  const adiposeKg = ((adiposeZ * 5.85) + 25.6) / ((170.18 / height) ** 3);
  const boneKg = headBoneMass + bodyBoneMass;
  const residualKg = ((residualZ * 1.24) + 6.1) / ((89.92 / sittingHeight) ** 3);
  const muscleKg = ((muscularZ * 5.4) + 24.5) / ((170.18 / height) ** 3);
  const structuredWeight = skinKg + adiposeKg + boneKg + residualKg + muscleKg;
  const adjust = (value) => structuredWeight ? value * (weight / structuredWeight) : 0;
  const adjustedSkinKg = adjust(skinKg);
  const adjustedAdiposeKg = adjust(adiposeKg);
  const adjustedBoneKg = adjust(boneKg);
  const adjustedResidualKg = adjust(residualKg);
  const adjustedMuscleKg = adjust(muscleKg);
  const adjustedTotalKg = adjustedSkinKg + adjustedAdiposeKg + adjustedBoneKg + adjustedResidualKg + adjustedMuscleKg;
  if ([skinKg, adiposeKg, boneKg, residualKg, muscleKg].some((value) => !Number.isFinite(value) || value <= 0)) {
    return { method: 'cinco_componentes', label: 'Cinco componentes', complete: false, missing: [], invalid: true };
  }
  const percentOfTotal = (value) => adjustedTotalKg ? (value * 100) / adjustedTotalKg : 0;

  return {
    method: "cinco_componentes",
    label: "Cinco componentes",
    complete: true,
    fatPercent: round(percentOfTotal(adjustedAdiposeKg), 1),
    fatKg: round(adjustedAdiposeKg, 2),
    leanKg: round(weight - adjustedAdiposeKg, 2),
    skinKg: round(adjustedSkinKg, 2),
    skinPercent: round(percentOfTotal(adjustedSkinKg), 1),
    adiposeKg: round(adjustedAdiposeKg, 2),
    adiposePercent: round(percentOfTotal(adjustedAdiposeKg), 1),
    boneKg: round(adjustedBoneKg, 2),
    bonePercent: round(percentOfTotal(adjustedBoneKg), 1),
    residualKg: round(adjustedResidualKg, 2),
    residualPercent: round(percentOfTotal(adjustedResidualKg), 1),
    muscleKg: round(adjustedMuscleKg, 2),
    musclePercent: round(percentOfTotal(adjustedMuscleKg), 1),
    totalKg: round(adjustedTotalKg, 2),
    structuredWeight: round(structuredWeight, 2),
    weightDifference: round(structuredWeight - weight, 2),
    weightDifferencePercent: round((structuredWeight - weight) * 100 / weight, 2),
    muscleBoneIndex: adjustedBoneKg ? round(adjustedMuscleKg / adjustedBoneKg, 2) : 0,
    adiposeMuscleIndex: adjustedMuscleKg ? round(adjustedAdiposeKg / adjustedMuscleKg, 2) : 0,
    skinfoldSum6: round(skinfoldSum6, 1),
    sourceRows: "Datos!E189:E222 (formato corregido, octubre 2026)",
    formula: "Ross & Kerr 1988 segun formato Excel actualizado: piel, adiposa, osea, residual y muscular ajustadas al peso.",
  };
}

function classifyBmi(bmi) {
  if (!bmi) return "";
  if (bmi < 18.5) return "Bajo peso para la talla";
  if (bmi < 25) return "Normal";
  if (bmi < 30) return "Sobrepeso";
  if (bmi < 35) return "Obesidad tipo I";
  if (bmi < 40) return "Obesidad tipo II";
  return "Obesidad tipo III";
}

function classifyBmiForAge(zScore) {
  if (!Number.isFinite(zScore)) return "";
  if (zScore < -3) return "Delgadez severa para la edad";
  if (zScore < -2) return "Delgadez para la edad";
  if (zScore <= 1) return "IMC adecuado para la edad";
  if (zScore <= 2) return "Sobrepeso para la edad";
  return "Obesidad para la edad";
}

function classifyHeightForAge(zScore) {
  if (!Number.isFinite(zScore)) return "";
  if (zScore < -3) return "Talla muy baja para la edad";
  if (zScore < -2) return "Talla baja para la edad";
  return "Talla adecuada para la edad";
}

function calculatePediatricGrowth(patient) {
  const ageYears = resolvePatientAge(patient);
  const weight = patientNumber(patient, "peso_kg");
  const height = patientNumber(patient, "talla_cm");
  const sittingHeight = patientNumber(patient, "estatura_sentado_cm");
  const legLength = height && sittingHeight ? height - sittingHeight : 0;
  const bmiAgeZScore = patientOptionalNumber(patient, "imc_edad_zscore");
  const heightAgeZScore = patientOptionalNumber(patient, "talla_edad_zscore");
  const centimetersToGrow = patientNumber(patient, "centimetros_por_crecer_cm");
  const enoughForMaturity = Boolean(
    ageYears > 0
    && weight
    && height
    && sittingHeight
    && legLength > 0,
  );
  let maturityOffset = 0;
  if (enoughForMaturity) {
    maturityOffset = sexIsMale(patient)
      ? -9.236
        + (0.0002708 * legLength * sittingHeight)
        - (0.001663 * ageYears * legLength)
        + (0.007216 * ageYears * sittingHeight)
        + (0.02292 * ((weight / height) * 100))
      : -9.376
        + (0.0001882 * legLength * sittingHeight)
        + (0.0022 * ageYears * legLength)
        + (0.005841 * ageYears * sittingHeight)
        - (0.002658 * ageYears * weight)
        + (0.07693 * ((weight / height) * 100));
  }
  const maturityStage = enoughForMaturity
    ? maturityOffset < -1
      ? "Antes del pico de crecimiento"
      : maturityOffset <= 1
        ? "En torno al pico de crecimiento"
        : "Después del pico de crecimiento"
    : "";
  return {
    complete: Boolean(ageYears && height),
    ageYears: round(ageYears, 1),
    bmiAgeZScore,
    bmiAgeClassification: classifyBmiForAge(bmiAgeZScore),
    heightAgeZScore,
    heightAgeClassification: classifyHeightForAge(heightAgeZScore),
    legLengthCm: round(legLength, 1),
    maturityComplete: enoughForMaturity,
    maturityOffsetYears: round(maturityOffset, 2),
    predictedAgeAtPhv: enoughForMaturity ? round(ageYears - maturityOffset, 2) : 0,
    maturityStage,
    centimetersToGrow: round(centimetersToGrow, 1),
    projectedAdultHeightCm: height && centimetersToGrow ? round(height + centimetersToGrow, 1) : 0,
    formula: enoughForMaturity
      ? sexIsMale(patient)
        ? "Mirwald: -9.236 + 0.0002708(LL×TS) - 0.001663(edad×LL) + 0.007216(edad×TS) + 0.02292(peso/talla×100)"
        : "Mirwald: -9.376 + 0.0001882(LL×TS) + 0.0022(edad×LL) + 0.005841(edad×TS) - 0.002658(edad×peso) + 0.07693(peso/talla×100)"
      : "",
    note: "Los Z-scores deben provenir de una referencia OMS por edad y sexo. La proyección de talla usa los centímetros por crecer registrados por el profesional.",
  };
}

function classifiedIndex(value, classifier) {
  return value
    ? { value: round(value, 2), classification: classifier(value) }
    : { value: 0, classification: "" };
}

function calculateProportionality(patient) {
  const height = patientNumber(patient, "talla_cm");
  const sittingHeight = patientNumber(patient, "estatura_sentado_cm");
  const wingspan = patientNumber(patient, "envergadura_cm");
  const biacromial = patientNumber(patient, "diametro_biacromial_cm");
  const bicristal = patientNumber(patient, "diametro_biliocrestal_cm");
  const acromialeRadiale = patientNumber(patient, "longitud_acromiale_radiale_cm");
  const radialeStylion = patientNumber(patient, "longitud_radiale_stylion_cm");
  const handLength = patientNumber(patient, "longitud_stylion_medio_dactylion_cm");
  const iliospinale = patientNumber(patient, "longitud_ilioespinale_cm");
  const cormic = height && sittingHeight ? (sittingHeight / height) * 100 : 0;
  const acromioIliac = biacromial && bicristal ? (bicristal / biacromial) * 100 : 0;
  const brachial = acromialeRadiale && radialeStylion ? (radialeStylion / acromialeRadiale) * 100 : 0;
  const relativeWingspan = height && wingspan ? (wingspan / height) * 100 : 0;
  const skeletal = sittingHeight && height ? ((height - sittingHeight) / sittingHeight) * 100 : 0;
  const upperLimb = height && acromialeRadiale && radialeStylion && handLength
    ? ((acromialeRadiale + radialeStylion + handLength) / height) * 100
    : 0;
  const lowerLimb = height && iliospinale ? (iliospinale / height) * 100 : 0;
  return {
    cormic: classifiedIndex(cormic, (value) => (
      value <= 51 ? "Braquicórmico o tronco corto"
        : value <= 53 ? "Metricórmico o tronco medio"
          : "Macrocórmico o tronco largo"
    )),
    acromioIliac: classifiedIndex(acromioIliac, (value) => (
      value < 70 ? "Tronco trapezoidal"
        : value < 75 ? "Tronco intermedio"
          : "Tronco rectangular"
    )),
    brachial: classifiedIndex(brachial, (value) => (
      value < 78 ? "Antebrazo corto"
        : value < 83 ? "Antebrazo medio"
          : "Antebrazo largo"
    )),
    relativeWingspan: classifiedIndex(relativeWingspan, (value) => (
      `Nivel ${Math.max(1, Math.min(7, Math.floor(value - 99) + 1))}`
    )),
    skeletal: classifiedIndex(skeletal, (value) => (
      value < 85 ? "Braquisquélico o extremidades inferiores cortas"
        : value < 90 ? "Mesosquélico o extremidades inferiores medianas"
          : "Macrosquélico o extremidades inferiores largas"
    )),
    relativeUpperLimb: classifiedIndex(upperLimb, (value) => (
      value < 45 ? "Extremidades superiores cortas"
        : value < 47 ? "Extremidades superiores medias"
          : "Extremidades superiores largas"
    )),
    relativeLowerLimb: classifiedIndex(lowerLimb, (value) => (
      value < 55 ? "Extremidades inferiores cortas"
        : value < 57 ? "Extremidades inferiores medias"
          : "Extremidades inferiores largas"
    )),
    source: "Datos filas 127-141 del formato antropométrico actualizado.",
  };
}

function classifySomatotype(endo, meso, ecto) {
  const values = [
    { key: "endo", label: "Endomorfo", value: endo },
    { key: "meso", label: "Mesomorfo", value: meso },
    { key: "ecto", label: "Ectomorfo", value: ecto },
  ].filter((item) => Number.isFinite(item.value));
  if (values.length < 3) return "";
  values.sort((a, b) => b.value - a.value);
  const [first, second] = values;
  if (Math.abs(first.value - second.value) < 0.8) return `${first.label}-${second.label}`;
  const modifierByKey = {
    endo: "endomorfo",
    meso: "mesomorfo",
    ecto: "ectomorfo",
  };
  if (first.key === "meso" && second.key === "endo") return "Meso endomorfo";
  if (first.key === "meso" && second.key === "ecto") return "Meso ectomorfo";
  if (first.key === "endo" && second.key === "meso") return "Endo mesomorfo";
  if (first.key === "endo" && second.key === "ecto") return "Endo ectomorfo";
  if (first.key === "ecto" && second.key === "meso") return "Ecto mesomorfo";
  if (first.key === "ecto" && second.key === "endo") return "Ecto endomorfo";
  return `${first.label} ${modifierByKey[second.key]}`;
}

function calculateSomatotype(patient) {
  const weight = patientNumber(patient, "peso_kg");
  const height = patientNumber(patient, "talla_cm");
  const triceps = patientNumber(patient, "pliegue_triceps_mm");
  const subscapular = patientNumber(patient, "pliegue_subescapular_mm");
  const supraespinal = patientNumber(patient, "pliegue_supraespinal_mm");
  const calfSkinfold = patientNumber(patient, "pliegue_pantorrilla_mm");
  const armTensed = patientNumber(patient, "perimetro_brazo_tenso_cm");
  const calf = patientNumber(patient, "perimetro_pantorrilla_cm");
  const humerus = patientNumber(patient, "diametro_humero_cm");
  const femur = patientNumber(patient, "diametro_femur_cm");
  if (!weight || !height) return { complete: false };

  const sum3 = triceps + subscapular + supraespinal;
  const correctedSum = sum3 && height ? (sum3 * 170.18) / height : 0;
  const endomorphy = correctedSum
    ? (-0.7182) + (0.1451 * correctedSum) - (0.00068 * correctedSum ** 2) + (0.0000014 * correctedSum ** 3)
    : 0;
  const ponderalIndex = height / (weight ** (1 / 3));
  const ectomorphy = ponderalIndex
    ? (ponderalIndex >= 40.75 ? (0.732 * ponderalIndex) - 28.58 : ponderalIndex > 38.25 ? (0.463 * ponderalIndex) - 17.63 : 0.1)
    : 0;
  const mesomorphy = humerus && femur && armTensed && calf
    ? (0.858 * humerus)
      + (0.601 * femur)
      + (0.188 * (armTensed - (triceps / 10)))
      + (0.161 * (calf - (calfSkinfold / 10)))
      - (0.131 * height)
      + 4.5
    : 0;

  const hasSomatotype = [triceps, subscapular, supraespinal, calfSkinfold, armTensed, calf, humerus, femur].every((value) => value > 0);
  return {
    complete: hasSomatotype,
    sum3: round(sum3, 1),
    correctedSum: round(correctedSum, 1),
    ponderalIndex: round(ponderalIndex, 2),
    endomorphy: round(endomorphy, 2),
    mesomorphy: round(mesomorphy, 2),
    ectomorphy: round(ectomorphy, 2),
    classification: hasSomatotype ? classifySomatotype(endomorphy, mesomorphy, ectomorphy) : "",
    x: hasSomatotype ? round(ectomorphy - endomorphy, 1) : 0,
    y: hasSomatotype ? round((2 * mesomorphy) - (ectomorphy + endomorphy), 1) : 0,
    formula: "X = ectomorfia - endomorfia; Y = 2 * mesomorfia - (ectomorfia + endomorfia)",
  };
}

function rawNumber(patient, fieldName) {
  const parsed = Number(patient?.[fieldName]);
  return Number.isFinite(parsed) ? parsed : 0;
}

function safePositive(value) {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function targetBase(method, label, complete, values = {}) {
  const kgAdiposeToLose = safePositive(values.kgAdiposeToLose);
  const kgMuscleToGain = safePositive(values.kgMuscleToGain);
  return {
    method,
    label,
    complete,
    ...values,
    kgAdiposeToLose: round(kgAdiposeToLose, 2),
    kgMuscleToGain: round(kgMuscleToGain, 2),
  };
}

function calculateTheoreticalWeight(patient) {
  const height = patientNumber(patient, "talla_cm");
  if (!height || (!sexIsMale(patient) && !sexIsFemale(patient))) return 0;
  const inchesOverFiveFeet = (height / 2.54) - 60;
  const value = sexIsMale(patient)
    ? 51.65 + (1.85 * inchesOverFiveFeet)
    : 48.67 + (1.65 * inchesOverFiveFeet);
  return round(value, 2);
}

function calculateYuhaszTarget(patient, yuhasz) {
  const expectedSum6 = patientNumber(patient, "meta_yuhasz_suma_6_pliegues_mm");
  const muscleChange = rawNumber(patient, "meta_yuhasz_cambio_muscular_kg");
  if (!expectedSum6 || !yuhasz?.complete || !yuhasz.leanKg) {
    return targetBase("yuhasz", "Metas Yuhasz", false, {
      expectedSum6,
      muscleChangeKg: muscleChange,
    });
  }
  const expectedFatPercent = sexIsMale(patient)
    ? (expectedSum6 * 0.1051) + 2.585
    : (expectedSum6 * 0.1548) + 3.58;
  const desiredWeight = yuhasz.leanKg / (1 - (expectedFatPercent / 100));
  const kgAdiposeToLose = patientNumber(patient, "peso_kg") - desiredWeight;
  const competitionWeight = desiredWeight + muscleChange;
  const height = patientNumber(patient, "talla_cm");
  return targetBase("yuhasz", "Metas Yuhasz", true, {
    expectedSum6: round(expectedSum6, 1),
    expectedFatPercent: round(expectedFatPercent, 1),
    desiredWeightKg: round(desiredWeight, 2),
    competitionWeightKg: round(competitionWeight, 2),
    resultingBmi: height ? round(competitionWeight / ((height / 100) ** 2), 1) : 0,
    muscleChangeKg: round(muscleChange, 2),
    kgAdiposeToLose,
    kgMuscleToGain: muscleChange,
    formula: "Excel Datos filas 227-235: grasa esperada Yuhasz, peso deseado y cambio muscular.",
  });
}

function calculateDurninTarget(patient, durninSiri) {
  const expectedSum4 = patientNumber(patient, "meta_durnin_suma_4_pliegues_mm");
  const muscleChange = rawNumber(patient, "meta_durnin_cambio_muscular_kg");
  if (!expectedSum4 || !durninSiri?.complete || !durninSiri.leanKg) {
    return targetBase("durnin_siri", "Metas Durnin + Siri", false, {
      expectedSum4,
      muscleChangeKg: muscleChange,
    });
  }
  const { c, m } = durninSiri.coefficients;
  const density = c - (m * Math.log10(expectedSum4));
  const expectedFatPercent = ((4.95 / density) - 4.5) * 100;
  const desiredWeight = durninSiri.leanKg / (1 - (expectedFatPercent / 100));
  const kgAdiposeToLose = patientNumber(patient, "peso_kg") - desiredWeight;
  const competitionWeight = desiredWeight + muscleChange;
  const height = patientNumber(patient, "talla_cm");
  return targetBase("durnin_siri", "Metas Durnin + Siri", true, {
    expectedSum4: round(expectedSum4, 1),
    density: round(density, 4),
    expectedFatPercent: round(expectedFatPercent, 1),
    desiredWeightKg: round(desiredWeight, 2),
    competitionWeightKg: round(competitionWeight, 2),
    resultingBmi: height ? round(competitionWeight / ((height / 100) ** 2), 1) : 0,
    muscleChangeKg: round(muscleChange, 2),
    kgAdiposeToLose,
    kgMuscleToGain: muscleChange,
    formula: "Excel Datos filas 237-243: DC esperada Durnin, Siri, peso deseado y cambio muscular.",
  });
}

function calculateRfmTarget(patient, rfm) {
  const idealWaist = patientNumber(patient, "meta_rfm_cintura_ideal_cm");
  const height = patientNumber(patient, "talla_cm");
  const weight = patientNumber(patient, "peso_kg");
  if (!idealWaist || !height || !weight || !rfm?.complete) {
    return targetBase("rfm", "Metas masa grasa relativa", false, { idealWaist });
  }
  const expectedFatPercent = sexIsMale(patient)
    ? 64 - (20 * height / idealWaist)
    : 76 - (20 * height / idealWaist);
  const expectedFatKg = (expectedFatPercent * weight) / 100;
  const expectedLeanKg = weight - expectedFatKg;
  const kgAdiposeToLose = (rfm.fatKg || 0) - expectedFatKg;
  const kgMuscleToGain = expectedLeanKg - (rfm.leanKg || 0);
  const desiredWeight = weight - kgAdiposeToLose + safePositive(kgMuscleToGain);
  return targetBase("rfm", "Metas masa grasa relativa", true, {
    idealWaist: round(idealWaist, 1),
    expectedFatPercent: round(expectedFatPercent, 1),
    expectedFatKg: round(expectedFatKg, 2),
    expectedLeanKg: round(expectedLeanKg, 2),
    desiredWeightKg: round(desiredWeight, 2),
    competitionWeightKg: round(desiredWeight, 2),
    resultingBmi: height ? round(desiredWeight / ((height / 100) ** 2), 1) : 0,
    kgAdiposeToLose,
    kgMuscleToGain,
    formula: "Excel Datos filas 244-250: cintura ideal, RFM ideal, grasa y masa libre objetivo.",
  });
}

function calculateFiveComponentTarget(patient, fiveComponents) {
  const expectedSum6 = patientNumber(patient, "meta_5c_suma_pliegues_ideal_mm");
  const targetImo = patientNumber(patient, "meta_5c_objetivo_imo") || fiveComponents?.muscleBoneIndex || 0;
  const height = patientNumber(patient, "talla_cm");
  const weight = patientNumber(patient, "peso_kg");
  if (!expectedSum6 || !targetImo || !height || !weight || !fiveComponents?.complete) {
    return targetBase("cinco_componentes", "Metas cinco componentes", false, {
      expectedSum6,
      targetImo,
    });
  }
  const heightScale = 170.18 / height;
  const adiposityIndex = ((expectedSum6 * heightScale) - 116.41) / 34.79;
  const expectedAdiposeKg = ((adiposityIndex * 5.85) + 25.6) / (heightScale ** 3);
  const kgAdiposeToLose = (fiveComponents.adiposeKg || 0) - expectedAdiposeKg;
  const expectedMuscleKg = (fiveComponents.boneKg || 0) * targetImo;
  const kgMuscleToGain = expectedMuscleKg - (fiveComponents.muscleKg || 0);
  const competitionWeight = weight - kgAdiposeToLose + safePositive(kgMuscleToGain);
  const expectedFatPercent = competitionWeight ? (expectedAdiposeKg / competitionWeight) * 100 : 0;
  return targetBase("cinco_componentes", "Metas cinco componentes", true, {
    expectedSum6: round(expectedSum6, 1),
    targetImo: round(targetImo, 2),
    expectedAdiposeKg: round(expectedAdiposeKg, 2),
    expectedFatPercent: round(expectedFatPercent, 1),
    expectedMuscleKg: round(expectedMuscleKg, 2),
    desiredWeightKg: round(competitionWeight, 2),
    competitionWeightKg: round(competitionWeight, 2),
    resultingBmi: height ? round(competitionWeight / ((height / 100) ** 2), 1) : 0,
    kgAdiposeToLose,
    kgMuscleToGain,
    formula: "Excel Datos filas 252-259: pliegues ideales, masa adiposa objetivo, IMO y PDC.",
  });
}

function calculateSkinfoldSumsTarget(patient) {
  const expectedTrunkSum6 = patientNumber(patient, "meta_tronco_suma_6_pliegues_mm");
  return targetBase("sumatoria_pliegues", "Meta de sumatoria de tronco", expectedTrunkSum6 > 0, {
    expectedTrunkSum6: round(expectedTrunkSum6, 1),
    note: "El formato actualizado define una meta por percentil para la suma de tronco, sin convertirla por sí sola en peso objetivo.",
  });
}

function calculateAnthropometricTargets(patient, compositions) {
  const targets = {
    yuhasz: calculateYuhaszTarget(patient, compositions.yuhasz),
    durnin_siri: calculateDurninTarget(patient, compositions.durninSiri),
    rfm: calculateRfmTarget(patient, compositions.rfm),
    cinco_componentes: calculateFiveComponentTarget(patient, compositions.fiveComponents),
    sumatoria_pliegues: calculateSkinfoldSumsTarget(patient),
  };
  const normalizedMethod = normalizeAnthropometryMethod(patient?.metodo_antropometria);
  const selectedKey = normalizedMethod;
  const selectedComposition = compositions[selectedKey];
  return {
    ...targets,
    selected: targets[selectedKey] || targetBase(
      selectedKey,
      `Metas ${selectedComposition?.label || "del método principal"}`,
      false,
      { note: "Este método no define una meta automática en el formato actualizado." },
    ),
  };
}

function chooseBasicFatComposition(patient, compositions) {
  const requested = normalizeAnthropometryMethod(patient?.metodo_antropometria);
  if (requested === "cinco_componentes" && compositions.yuhasz?.fatPercent) return compositions.yuhasz;
  const selected = compositions[requested];
  if (selected?.fatPercent) return selected;
  if (requested === "sumatoria_pliegues") return compositions.yuhasz;
  return Object.values(compositions).find((item) => item?.complete && item?.fatPercent) || null;
}

function chooseLeanMass(patient, compositions, primaryComposition) {
  const requested = String(patient?.fuente_masa_libre_grasa || "rfm");
  const candidates = {
    metodo_principal: primaryComposition,
    rfm: compositions.rfm,
    durnin_siri: compositions.durninSiri,
    cinco_componentes: compositions.fiveComponents,
    slaughter: compositions.slaughter,
    parizkova: compositions.parizkova,
    brook_lohman: compositions.brookLohman,
    durnin_rahaman_lohman: compositions.durninRahamanLohman,
  };
  const selected = candidates[requested]?.leanKg ? candidates[requested] : Object.values(candidates).find((item) => item?.leanKg);
  return {
    value: selected?.leanKg ? round(selected.leanKg, 2) : 0,
    source: selected?.method || requested,
    label: selected?.label || leanMassSourceOptions.find((item) => item.value === requested)?.label || "Masa libre de grasa",
  };
}

function calculateDerivedBasics(patient, compositions, targets, primaryComposition) {
  const fatComposition = chooseBasicFatComposition(patient, compositions);
  const leanMass = chooseLeanMass(patient, compositions, primaryComposition);
  const targetWeight = targets.selected?.competitionWeightKg || targets.selected?.desiredWeightKg || 0;
  return {
    idealWeightKg: round(targetWeight || calculateTheoreticalWeight(patient), 2),
    idealWeightSource: targetWeight ? `metas_${targets.selected.method}` : "peso_teorico_talla_genero",
    objectiveWeightKg: patientNumber(patient, "peso_objetivo_kg"),
    fatPercent: fatComposition?.fatPercent ? round(fatComposition.fatPercent, 1) : 0,
    fatPercentSource: fatComposition?.method || "",
    leanMass,
  };
}

function selectPrimaryComposition(patient, methods) {
  const preferred = normalizeAnthropometryMethod(patient?.metodo_antropometria);
  const byMethod = new Map(methods.map((method) => [method.method, method]));
  const selected = byMethod.get(preferred);
  if (selected) return selected;
  return methods.find((method) => method.method !== "sumatoria_pliegues" && method.complete && (method.fatPercent || method.adiposePercent)) || null;
}

export function calculateAnthropometry(patient) {
  const weight = patientNumber(patient, "peso_kg");
  const height = patientNumber(patient, "talla_cm");
  const bmi = weight && height ? weight / ((height / 100) ** 2) : 0;
  const ageYears = resolvePatientAge(patient);
  const yuhasz = calculateYuhasz(patient);
  const durninSiri = calculateDurninSiri(patient);
  const rfm = calculateRfm(patient);
  const slaughter = calculateSlaughter(patient);
  const parizkova = calculateParizkova(patient);
  const brookLohman = calculateBrookLohman(patient);
  const durninRahamanLohman = calculateDurninRahamanLohman(patient);
  const fiveComponents = calculateFiveComponents(patient);
  const skinfoldSums = calculateSkinfoldSums(patient);
  const somatotype = calculateSomatotype(patient);
  const compositionByMethod = {
    yuhasz,
    durnin_siri: durninSiri,
    durninSiri,
    rfm,
    slaughter,
    parizkova,
    brook_lohman: brookLohman,
    brookLohman,
    durnin_rahaman_lohman: durninRahamanLohman,
    durninRahamanLohman,
    cinco_componentes: fiveComponents,
    fiveComponents,
  };
  const primaryComposition = selectPrimaryComposition(patient, [
    skinfoldSums,
    fiveComponents,
    slaughter,
    parizkova,
    brookLohman,
    durninRahamanLohman,
    yuhasz,
    durninSiri,
    rfm,
  ]);
  const targets = calculateAnthropometricTargets(patient, compositionByMethod);
  const derivedBasics = calculateDerivedBasics(patient, compositionByMethod, targets, primaryComposition);
  const pediatricGrowth = calculatePediatricGrowth(patient);
  const proportionality = calculateProportionality(patient);
  const leanMassForIndex = primaryComposition?.leanKg || derivedBasics.leanMass?.value || 0;
  const activeSubstanceIndex = height && leanMassForIndex
    ? round((leanMassForIndex * 100000) / (height ** 3), 3)
    : 0;
  const suggestedPediatricMethod = ageYears <= 0
    ? ""
    : ageYears < 12
      ? "brook_lohman"
      : ageYears < 17
        ? "durnin_rahaman_lohman"
        : "slaughter";

  return {
    ageYears,
    bmi: round(bmi, 1),
    bmiClassification: classifyBmi(bmi),
    waistToHeight: height ? round(patientNumber(patient, "perimetro_cintura_cm") / height, 2) : 0,
    activeSections: getActiveAnthropometrySections(patient),
    yuhasz,
    durninSiri,
    rfm,
    slaughter,
    parizkova,
    brookLohman,
    durninRahamanLohman,
    fiveComponents,
    skinfoldSums,
    primaryComposition,
    somatotype,
    pediatricGrowth,
    proportionality,
    activeSubstanceIndex,
    suggestedPediatricMethod,
    targets,
    derivedBasics,
    source: "Formato actualizado antropometria programa dietas.xlsx",
    formulaProfile: "formato_antropometria_2026",
    formulaVersion: "3.1.0",
  };
}
